import * as React from 'react'
import { useMemo, useState } from 'react'
import clx from 'classnames'
import { Button } from '@/ui/button'
import { Dropdown, type Option } from '@/ui/form/controls/Dropdown'
import { AdminIcon } from '@/modules/shared/components'
import { CheckboxField } from '@/modules/shared/components/CheckboxField'
import AddUserColor from '@/assets/icons_new/AddUserColor.svg'
import {
  ServiceAccountAccess,
  ServiceAccountAccessEntry,
  ServiceAccountAccessUser,
  ServiceAccountChild,
} from '@/modules/shared/query-client/useServiceAccountAdmin'
import { displayName, hasAccountWideAccess, inputClass, rolesIn, sameIds } from './shared'

type Save = (entries: ServiceAccountAccessEntry[]) => Promise<boolean>

// null = osoba nie ma dostępu do firmy; [] = zaznaczona, ale bez wybranej roli.
type Draft = Map<string, number[] | null>

const newPerson = (login: string, firstName: string, surname: string): ServiceAccountAccessUser => ({
  userId: -1,
  login,
  firstName,
  surname,
  hasKeycloakAccount: true,
  lastLogin: null,
  rosterRole: null,
  accountRoles: [],
  companyRoles: [],
  profileCompanyIds: [],
})

const byName = (a: ServiceAccountAccessUser, b: ServiceAccountAccessUser) =>
  (displayName(a) || a.login).localeCompare(displayName(b) || b.login, 'pl')

const matches = (u: ServiceAccountAccessUser, needle: string) =>
  !needle || u.login.toLowerCase().includes(needle) || displayName(u).toLowerCase().includes(needle)

/** Kim osoba jest dla konta: użytkownik konta widzi wszystkie firmy, reszta tylko przypisane. */
export const PersonKindTag: React.FC<{ user: ServiceAccountAccessUser }> = ({ user }) =>
  hasAccountWideAccess(user) ? (
    <span
      className="tw-whitespace-nowrap tw-rounded tw-bg-primary tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-content-primary"
      title="Użytkownik konta usługowego - widzi wszystkie firmy konta"
    >
      {user.rosterRole === 'owner' ? 'Właściciel konta' : 'Użytkownik konta'}
    </span>
  ) : (
    <span
      className="tw-whitespace-nowrap tw-rounded tw-border tw-border-base-300 tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-text-content-base"
      title="Dodany do wybranych firm - widzi tylko firmy, w których ma rolę"
    >
      wybrane firmy
    </span>
  )

// Po lewej wszyscy związani z kontem - użytkownicy konta i osoby dodane do pojedynczych firm,
// po prawej firmy konta do zaznaczenia.
export const AccessPeople: React.FC<{
  access: ServiceAccountAccess
  accountChildren: ServiceAccountChild[]
  roleOptions: Option[]
  busy: boolean
  onSave: Save
}> = ({ access, accountChildren, roleOptions, busy, onSave }) => {
  const [search, setSearch] = useState('')
  const [draftPerson, setDraftPerson] = useState<ServiceAccountAccessUser | null>(null)
  const [selectedLogin, setSelectedLogin] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const people = useMemo(() => {
    const list = [...access.users]
    if (draftPerson && !list.some((u) => u.login.toLowerCase() === draftPerson.login.toLowerCase()))
      list.push(draftPerson)
    return list
  }, [access.users, draftPerson])

  const needle = search.trim().toLowerCase()
  const accountUsers = people.filter((u) => hasAccountWideAccess(u) && matches(u, needle)).sort(byName)
  const companyUsers = people.filter((u) => !hasAccountWideAccess(u) && matches(u, needle)).sort(byName)
  const visible = [...accountUsers, ...companyUsers]
  const selected =
    people.find((u) => u.login.toLowerCase() === selectedLogin?.toLowerCase()) ??
    companyUsers[0] ??
    visible[0] ??
    null

  const section = (title: string, hint: string, users: ServiceAccountAccessUser[]) =>
    users.length > 0 && (
      <>
        <li className="tw-sticky tw-top-0 tw-z-10 tw-border-b tw-border-base-300 tw-bg-base-200 tw-px-3 tw-py-1.5">
          <div className="tw-text-xs tw-font-medium tw-uppercase tw-text-content-base tw-opacity-80">
            {title} ({users.length})
          </div>
          <div className="tw-text-[11px] tw-text-content-base tw-opacity-60">{hint}</div>
        </li>
        {users.map((u) => {
          const name = displayName(u)
          return (
            <li
              key={u.login}
              onClick={() => setSelectedLogin(u.login)}
              className={clx(
                'tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-b tw-border-base-300/50 tw-px-3 tw-py-2 tw-text-sm tw-transition-colors hover:tw-bg-primary/10',
                selected?.login === u.login && 'tw-bg-primary/20 tw-font-medium',
              )}
            >
              <AdminIcon name="profil" className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-text-content-base" />
              <div className="tw-min-w-0 tw-flex-1">
                <div className="tw-truncate tw-text-content-base" title={name || u.login}>
                  {name || u.login}
                </div>
                {name && (
                  <div className="tw-truncate tw-text-xs tw-font-normal tw-text-content-base tw-opacity-60">
                    {u.login}
                  </div>
                )}
              </div>
              <span className="tw-flex-shrink-0 tw-text-xs tw-font-normal tw-text-content-base tw-opacity-70">
                {u.userId < 0 ? 'nowa' : `${u.companyRoles.length}/${access.companies.length}`}
              </span>
            </li>
          )
        })}
      </>
    )

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1">
      <aside className="tw-flex tw-w-[300px] tw-flex-shrink-0 tw-flex-col tw-border-r tw-border-base-300">
        <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 tw-border-b tw-border-base-300 tw-px-2 tw-py-1.5">
          <button
            type="button"
            className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-p-2 hover:tw-bg-base-300 hover:tw-opacity-90"
            onClick={() => setAdding((v) => !v)}
          >
            <img src={AddUserColor} alt="" />
            <span className="tw-text-sm tw-font-medium tw-text-primary">Dodaj osobę</span>
          </button>
        </div>
        {adding && (
          <NewPersonForm
            known={(login) => access.users.some((u) => u.login.toLowerCase() === login.toLowerCase())}
            onCancel={() => setAdding(false)}
            onAdd={(person) => {
              setDraftPerson(person)
              setSelectedLogin(person.login)
              setAdding(false)
            }}
          />
        )}
        <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-3 tw-border-b tw-border-base-300 tw-px-4 tw-py-1">
          <AdminIcon name="search" />
          <input
            className="tw-w-full tw-border-0 tw-bg-transparent tw-py-2 tw-text-sm tw-outline-none"
            placeholder="Szukaj osoby…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <ul className="custom-scrollbar tw-relative tw-flex-1 tw-overflow-y-auto">
          {section('Użytkownicy konta', 'widzą wszystkie firmy konta', accountUsers)}
          {section('Dodani do wybranych firm', 'widzą tylko zaznaczone firmy', companyUsers)}
          {visible.length === 0 && (
            <li className="tw-p-4 tw-text-center tw-text-xs tw-text-content-base">
              {people.length === 0 ? 'Brak osób w firmach konta.' : 'Brak osób pasujących do wyszukiwania.'}
            </li>
          )}
        </ul>
      </aside>

      <section className="tw-min-w-0 tw-flex-1">
        {selected ? (
          <PersonCompanies
            key={selected.login}
            user={selected}
            companies={access.companies}
            accountChildren={accountChildren}
            roleOptions={roleOptions}
            busy={busy}
            onSave={async (entries) => {
              const ok = await onSave(entries)
              if (ok && selected === draftPerson) setDraftPerson(null)
              return ok
            }}
          />
        ) : (
          <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm tw-text-content-base tw-opacity-70">
            Wybierz osobę z listy albo dodaj nową.
          </div>
        )}
      </section>
    </div>
  )
}

const NewPersonForm: React.FC<{
  known: (login: string) => boolean
  onAdd: (person: ServiceAccountAccessUser) => void
  onCancel: () => void
}> = ({ known, onAdd, onCancel }) => {
  const [login, setLogin] = useState('')
  const [firstName, setFirstName] = useState('')
  const [surname, setSurname] = useState('')
  // Dla osoby spoza konta serwer wymaga imienia i nazwiska - także gdy ma już konto gdzie indziej.
  const valid =
    login.includes('@') && (known(login.trim()) || (!!firstName.trim() && !!surname.trim()))
  return (
    <div className="tw-flex tw-flex-shrink-0 tw-flex-col tw-gap-2 tw-border-b tw-border-base-300 tw-bg-base-200/40 tw-p-3">
      <input
        className={inputClass}
        type="email"
        placeholder="Adres e-mail"
        value={login}
        onChange={(e) => setLogin(e.target.value)}
        autoFocus
      />
      <div className="tw-flex tw-gap-2">
        <input className={inputClass} placeholder="Imię" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        <input className={inputClass} placeholder="Nazwisko" value={surname} onChange={(e) => setSurname(e.target.value)} />
      </div>
      <div className="tw-flex tw-justify-end tw-gap-2">
        <Button size="sm" variant="tertiary" onClick={onCancel}>
          Anuluj
        </Button>
        <Button
          size="sm"
          disabled={!valid}
          onClick={() => onAdd(newPerson(login.trim(), firstName.trim(), surname.trim()))}
        >
          Dalej
        </Button>
      </div>
    </div>
  )
}

// Najczęstsza rola osoby - domyślna przy zaznaczaniu kolejnych firm.
const mostCommonRole = (u: ServiceAccountAccessUser, options: Option[]): number[] => {
  const counts = new Map<number, number>()
  u.companyRoles.forEach((c) => c.roles.forEach((r) => counts.set(r.id, (counts.get(r.id) ?? 0) + 1)))
  const best = [...counts.entries()]
    .filter(([id]) => options.some((o) => o.value === String(id)))
    .sort((a, b) => b[1] - a[1])[0]
  return best ? [best[0]] : []
}

const PersonCompanies: React.FC<{
  user: ServiceAccountAccessUser
  companies: Array<{ id: string; name: string }>
  accountChildren: ServiceAccountChild[]
  roleOptions: Option[]
  busy: boolean
  onSave: Save
}> = ({ user, companies, accountChildren, roleOptions, busy, onSave }) => {
  const initial = useMemo<Draft>(
    () =>
      new Map(
        companies.map((c) => {
          const roles = rolesIn(user, c.id)
          return [c.id, roles.length > 0 ? roles : null]
        }),
      ),
    [user, companies],
  )
  const [draft, setDraft] = useState<Draft>(initial)
  const [defaultRoles, setDefaultRoles] = useState<number[]>(() => mostCommonRole(user, roleOptions))

  React.useEffect(() => setDraft(initial), [initial])
  React.useEffect(() => {
    if (defaultRoles.length === 0) setDefaultRoles(mostCommonRole(user, roleOptions))
  }, [roleOptions]) // eslint-disable-line react-hooks/exhaustive-deps

  // Role, których nie ma na liście (np. kierowca), zostają widoczne u osoby, która je ma.
  const options = useMemo(() => {
    const extra = new Map<string, string>()
    user.companyRoles.forEach((c) =>
      c.roles.forEach((r) => {
        if (!roleOptions.some((o) => o.value === String(r.id))) extra.set(String(r.id), r.name)
      }),
    )
    return [...roleOptions, ...[...extra].map(([value, label]) => ({ value, label }))]
  }, [roleOptions, user])

  const isChanged = (id: string) => {
    const a = draft.get(id) ?? null
    const b = initial.get(id) ?? null
    return a === null || b === null ? a !== b : !sameIds(a, b)
  }
  const changed = companies.filter((c) => isChanged(c.id))
  const missingRole = companies.filter((c) => draft.get(c.id)?.length === 0)
  const memberCount = companies.filter((c) => draft.get(c.id) != null).length
  const allChecked = companies.length > 0 && memberCount === companies.length
  const nip = (id: string) => accountChildren.find((ch) => ch.id === id)?.nip
  const accountWide = hasAccountWideAccess(user)

  const set = (ids: string[], value: (id: string) => number[] | null) =>
    setDraft((prev) => {
      const next = new Map(prev)
      ids.forEach((id) => next.set(id, value(id)))
      return next
    })

  const check = (ids: string[]) => set(ids, (id) => draft.get(id) ?? initial.get(id) ?? [...defaultRoles])
  const toggle = (id: string) => (draft.get(id) != null ? set([id], () => null) : check([id]))

  const save = () =>
    onSave(
      changed.map((c) => ({
        login: user.login,
        firstName: user.firstName ?? undefined,
        surname: user.surname ?? undefined,
        companyId: c.id,
        roleIds: draft.get(c.id) ?? [],
      })),
    )

  const name = displayName(user)

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-bg-content-primary">
      <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-3 tw-border-b tw-border-base-300 tw-px-4 tw-py-3">
        <AdminIcon name="profil" className="tw-h-4 tw-w-4 tw-text-primary" />
        <div className="tw-min-w-0">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <span className="tw-truncate tw-font-medium tw-text-content-base">{name || user.login}</span>
            <PersonKindTag user={user} />
            {user.userId < 0 && (
              <span
                className="tw-rounded tw-border tw-border-success tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-success"
                title="Po zapisie istniejące konto dostanie role, a osoba bez konta - e-mail z linkiem do ustawienia hasła"
              >
                spoza listy - bez konta dostanie zaproszenie
              </span>
            )}
          </div>
          {name && <div className="tw-text-xs tw-text-content-base tw-opacity-60">{user.login}</div>}
        </div>
        <span className="tw-ml-auto tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 tw-text-sm tw-text-content-base">
          <AdminIcon name="company-group" />
          {memberCount}/{companies.length}
        </span>
      </div>

      {accountWide && (
        <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 tw-border-b tw-border-base-300 tw-bg-primary/10 tw-px-4 tw-py-2 tw-text-xs tw-text-content-base">
          <AdminIcon name="help" className="tw-text-primary" />
          Użytkownik konta widzi wszystkie firmy konta. Role zaznaczone poniżej działają dodatkowo, w
          konkretnych firmach.
        </div>
      )}

      <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-3 tw-border-b tw-border-base-300 tw-px-4 tw-py-1.5">
        <span className="tw-text-sm tw-text-content-base">Rola dla zaznaczanych firm:</span>
        <div className="tw-w-60">
          <Dropdown
            compact
            portalMenu
            multiple
            showSelectedNames
            options={roleOptions}
            value={defaultRoles.map(String)}
            onChange={(values: string[]) => setDefaultRoles(values.map(Number))}
            placeholder="Wybierz rolę"
          />
        </div>
      </div>

      <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
        <table className="tw-w-full tw-border-collapse">
          <thead className="tw-sticky tw-top-0 tw-z-10 tw-bg-content-primary">
            <tr>
              <th className="tw-w-12 tw-border-b tw-border-base-300 tw-px-4 tw-py-3">
                <CheckboxField
                  id="access-company-all"
                  checked={allChecked}
                  disabled={busy}
                  onChange={() =>
                    allChecked ? set(companies.map((c) => c.id), () => null) : check(companies.map((c) => c.id))
                  }
                />
              </th>
              {['Firma', 'NIP', 'Role w firmie'].map((header) => (
                <th
                  key={header}
                  className="tw-border-b tw-border-base-300 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {companies.map((c) => {
              const roles = draft.get(c.id)
              const checked = roles != null
              const rowChanged = isChanged(c.id)
              const profileOnly = !checked && initial.get(c.id) == null && user.profileCompanyIds.includes(c.id)
              return (
                <tr
                  key={c.id}
                  onClick={() => !busy && toggle(c.id)}
                  className={clx(
                    'tw-cursor-pointer tw-border-b tw-border-base-300 hover:tw-bg-primary/10',
                    checked && 'tw-bg-primary/10 tw-font-medium',
                  )}
                >
                  <td className="tw-w-12 tw-px-4 tw-py-3" onClick={(e) => e.stopPropagation()}>
                    <CheckboxField
                      id={`access-company-${c.id}`}
                      checked={checked}
                      disabled={busy}
                      onChange={() => toggle(c.id)}
                    />
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-content-base">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      {c.name}
                      {rowChanged && (
                        <span className="tw-rounded tw-bg-warning tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-amber">
                          {checked ? (initial.get(c.id) == null ? 'nadanie' : 'zmiana') : 'odebranie'}
                        </span>
                      )}
                    </div>
                    {profileOnly && (
                      <div className="tw-text-xs tw-font-normal tw-italic tw-opacity-60">profil bez roli - brak dostępu</div>
                    )}
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-font-normal tw-text-content-base">{nip(c.id) || '—'}</td>
                  <td className="tw-w-80 tw-px-4 tw-py-2" onClick={(e) => e.stopPropagation()}>
                    {checked && (
                      <Dropdown
                        compact
                        portalMenu
                        multiple
                        showSelectedNames
                        options={options}
                        value={(roles ?? []).map(String)}
                        onChange={(values: string[]) => set([c.id], () => values.map(Number))}
                        placeholder="Wybierz rolę"
                        disabled={busy}
                        triggerClassName={roles?.length === 0 ? '!tw-border-error' : undefined}
                      />
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Przyciski po lewej: prawy dolny róg zajmuje pływająca ikona zgód na cookies. */}
      <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 tw-border-t tw-border-base-300 tw-px-4 tw-py-2">
        <Button
          size="sm"
          loading={busy}
          disabled={busy || changed.length === 0 || missingRole.length > 0}
          onClick={save}
        >
          Zapisz
        </Button>
        {changed.length > 0 && (
          <Button size="sm" variant="tertiary" disabled={busy} onClick={() => setDraft(initial)}>
            Odrzuć
          </Button>
        )}
        <span className="tw-ml-2 tw-text-sm tw-text-content-base tw-opacity-80">
          {changed.length === 0 ? 'Brak zmian' : `Zmiany do zapisania: ${changed.length}`}
        </span>
        {missingRole.length > 0 && (
          <span className="tw-text-sm tw-text-error">· wybierz rolę w zaznaczonych firmach ({missingRole.length})</span>
        )}
      </div>
    </div>
  )
}
